import { atom } from "jotai";
import { Pog, PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import {
  BayRowOrder,
  getPatternRowDirections,
  getTraversalRows,
  inferSnakingPattern,
  RowDirection,
  SnakingPattern,
  SnakingPog,
} from "../common/snakingInference";
import {
  getDuplicateCdtPaths,
  type CdtDuplicatePath,
  type CdtPriorityLevel,
} from "../components/Panel/panels/CdtPriorityOrder/store/utils";

export interface CdtEditorSessionState {
  pattern: SnakingPattern;
  hasPriorityOrderViolation: boolean;
  duplicateCdtPaths?: CdtDuplicatePath[];
  hasUnsupportedFixtures: boolean;
  isSnakingConfirmationOpen: boolean;
  /** Stable identity list for all Traversal Rows, including empty pegboard rows. */
  knownRowIds?: string[];
  /** @deprecated Kept for session snapshots created before mixed-fixture traversal. */
  knownShelfIds?: string[];
  knownBayIds?: string[];
}

export interface CdtSnakingInferenceRequest {
  pog: PogNonCircular;
}

export const cdtEditorSessionAtom = atom<CdtEditorSessionState | null>(null);

export const cdtSnakingInferenceRequestAtom = atom<CdtSnakingInferenceRequest | null>(null);

export const cdtEditorActiveLevelAtom = atom<CdtPriorityLevel>("cdt3");

export const cdtSelectedGroupAtom = atom<string | null>(null);

interface SynchronizedSnakingPattern {
  pattern: SnakingPattern;
  hasNewPatternEntry: boolean;
  knownRowIds: string[];
  knownShelfIds: string[];
  knownBayIds: string[];
}

const hasSameValues = (left: Record<string, string> | undefined, right: Record<string, string>) => {
  const leftValues = left ?? {};
  const leftKeys = Object.keys(leftValues);
  const rightKeys = Object.keys(right);

  return (
    leftKeys.length === rightKeys.length && leftKeys.every((key) => leftValues[key] === right[key])
  );
};

const getRegularShelfIds = (pog: SnakingPog) =>
  getTraversalRows(pog)
    .filter((row) => row.kind === "shelf")
    .flatMap((row) => row.logicalShelf?.shelves.map((shelf) => shelf.uniqueId) ?? []);

const oppositeDirection = (direction: RowDirection): RowDirection =>
  direction === "LTR" ? "RTL" : "LTR";

const createPattern = (
  rowDirections: Record<string, RowDirection>,
  bayShelfOrders: Record<string, BayRowOrder>,
): SnakingPattern => ({ bayShelfOrders, rowDirections });

const getEmptyPegboardRowDirection = (
  rowId: string,
  rows: ReturnType<typeof getTraversalRows>,
  currentDirections: Record<string, RowDirection>,
): RowDirection => {
  const row = rows.find((candidate) => candidate.id === rowId);
  if (!row?.pegboard || row.holeNoTop === undefined) return "LTR";

  const pegboardRows = rows
    .filter(
      (candidate) =>
        candidate.kind === "pegboard" && candidate.pegboard?.uniqueId === row.pegboard?.uniqueId,
    )
    .sort((first, second) => (first.holeNoTop ?? 0) - (second.holeNoTop ?? 0));
  const rowIndex = pegboardRows.findIndex((candidate) => candidate.id === rowId);
  const preceding = [...pegboardRows.slice(0, rowIndex)]
    .reverse()
    .find((candidate) => currentDirections[candidate.id] !== undefined);
  if (preceding) {
    const precedingDirection = currentDirections[preceding.id];
    return precedingDirection ? oppositeDirection(precedingDirection) : "LTR";
  }

  const followingIndex = pegboardRows
    .slice(rowIndex + 1)
    .findIndex((candidate) => currentDirections[candidate.id] !== undefined);
  const following = followingIndex >= 0 ? pegboardRows[rowIndex + 1 + followingIndex] : undefined;
  if (!following) return "LTR";

  const distance = followingIndex + 1;
  const direction = currentDirections[following.id];
  if (!direction) return "LTR";
  return distance % 2 === 0 ? direction : oppositeDirection(direction);
};

export const synchronizeCdtSnakingPattern = (
  pog: SnakingPog,
  pattern: SnakingPattern,
  knownRowIds: string[] = [],
  knownBayIds: string[] = pog.planogram.bays.map((bay) => bay.uniqueId),
): SynchronizedSnakingPattern => {
  const rows = getTraversalRows(pog);
  const rowDirections: Record<string, RowDirection> = {};
  const bayShelfOrders: Record<string, BayRowOrder> = {};
  const currentDirections = getPatternRowDirections(pattern);
  const previousRowIds = new Set(knownRowIds);
  const previousBayIds = new Set(knownBayIds);
  let hasNewPatternEntry = false;

  for (const row of rows) {
    const direction = currentDirections[row.id];
    if (direction) rowDirections[row.id] = direction;
  }

  // Seed new occupied rows before deriving empty pegboard-row fallbacks, so
  // a newly added board can still alternate its empty metadata deterministically.
  for (const row of rows) {
    if (rowDirections[row.id] || (row.kind === "pegboard" && row.items.length === 0)) continue;
    if (!previousRowIds.has(row.id)) {
      rowDirections[row.id] = "LTR";
      hasNewPatternEntry = true;
    }
  }

  for (const row of rows) {
    if (row.kind === "pegboard" && row.items.length === 0 && !rowDirections[row.id]) {
      rowDirections[row.id] = getEmptyPegboardRowDirection(row.id, rows, rowDirections);
    }
  }

  for (const bay of pog.planogram.bays) {
    const order = pattern.bayShelfOrders[bay.uniqueId];
    if (order) {
      bayShelfOrders[bay.uniqueId] = order;
    } else if (!previousBayIds.has(bay.uniqueId)) {
      bayShelfOrders[bay.uniqueId] = "TopFirst";
      hasNewPatternEntry = true;
    }
  }

  const patternChanged =
    !hasSameValues(pattern.rowDirections, rowDirections) ||
    !hasSameValues(pattern.bayShelfOrders, bayShelfOrders);

  return {
    pattern: patternChanged ? createPattern(rowDirections, bayShelfOrders) : pattern,
    hasNewPatternEntry,
    knownRowIds: rows.map((row) => row.id),
    knownShelfIds: getRegularShelfIds(pog),
    knownBayIds: pog.planogram.bays.map((bay) => bay.uniqueId),
  };
};

export const createCdtEditorSessionState = (pog: Pog): CdtEditorSessionState => {
  const result = inferSnakingPattern(pog);
  const rows = getTraversalRows(pog);

  return {
    pattern: result.pattern,
    hasPriorityOrderViolation: result.hasPriorityOrderViolation,
    duplicateCdtPaths: getDuplicateCdtPaths(pog),
    hasUnsupportedFixtures: result.hasUnsupportedFixtures,
    isSnakingConfirmationOpen: !result.hasUnsupportedFixtures && !result.isConfident,
    knownRowIds: rows.map((row) => row.id),
    knownShelfIds: getRegularShelfIds(pog),
    knownBayIds: pog.planogram.bays.map((bay) => bay.uniqueId),
  };
};
